import { Box, Grid2 as Grid, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useState } from "react";
import { Button } from "../../common";
import { EditIcon } from "../../icons";
import {
  createOrganizationWizardStepAtom,
  isCreateOrganizationSuccessAtom,
  organizationWizardWashTypesAtom,
  selectedOrgIdAtom,
  toastOptionsAtom,
} from "../../../atoms";
import {
  useCreateDefaultWashTypesMutation,
  useFetchOrganizationInfoQuery,
} from "../../../api";
import { OrganizationModel } from "../../../models";
import { theme } from "../../../utils";
import { COUNTRIES } from "../../../constants";

const ReviewRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      justifyContent: "space-between",
      py: 1,
      gap: 1,
      alignItems: { sm: "center" },
    }}
  >
    <Typography sx={{ color: "#a6a6a6", fontSize: "14px" }}>{label}</Typography>
    <Typography sx={{ fontSize: "14px" }}>
      {value !== undefined && value !== null && value !== ""
        ? value
        : "Not provided"}
    </Typography>
  </Box>
);

export const OrganizationReviewStep = () => {
  const [, setStep] = useAtom(createOrganizationWizardStepAtom);
  const [, setIsSuccess] = useAtom(isCreateOrganizationSuccessAtom);
  const [washTypes] = useAtom(organizationWizardWashTypesAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [isCreating, setIsCreating] = useState(false);

  const fetchOrganizationInfoQuery = useFetchOrganizationInfoQuery(
    selectedOrgId || 0,
    { enabled: !!selectedOrgId && selectedOrgId !== 0 },
  );
  const createDefaultWashTypesMutation = useCreateDefaultWashTypesMutation();

  const profileInfo = fetchOrganizationInfoQuery.data
    ? OrganizationModel.buildOrganizationProfile(
        fetchOrganizationInfoQuery.data,
      )
    : undefined;
  const paymentReminderInfo = fetchOrganizationInfoQuery.data?.paymentReminder;

  const enabledWashTypes = washTypes.filter((wt) => wt.enabled);
  const washTypesToCreate = enabledWashTypes.filter((wt) => !wt.isCreated);

  const handleCreate = async () => {
    if (!selectedOrgId) return;

    setIsCreating(true);

    try {
      if (washTypesToCreate.length > 0) {
        await createDefaultWashTypesMutation.mutateAsync({
          orgId: selectedOrgId,
          defaultWashTypeIds: washTypesToCreate
            .map((wt) => wt.id)
            .filter((id): id is number => id !== undefined),
        });
      }

      setIsSuccess(true);
    } catch {
      setToastOptions({
        open: true,
        message: "Something went wrong creating wash types",
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Grid
      container
      direction="column"
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
      spacing={{ xs: 2, sm: 3 }}
    >
      <Grid
        size={12}
        sx={{
          border: "1px solid #E5E5E5",
          borderRadius: "8px",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            mb: 1,
            gap: 1,
            alignItems: { sm: "center" },
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Organization Details</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={() => setStep(0)}
          >
            <EditIcon width="16px" active />
            <Typography
              sx={{ color: theme.palette.primary.main, fontSize: "14px" }}
            >
              Edit
            </Typography>
          </Box>
        </Box>
        <ReviewRow label="Organization Name" value={profileInfo?.name} />
        <ReviewRow
          label="Country"
          value={
            COUNTRIES.find((c) => c.value === profileInfo?.country)?.key ||
            profileInfo?.country
          }
        />
        <ReviewRow label="Contact Person" value={profileInfo?.contactPerson} />
        <ReviewRow label="Mobile Number" value={profileInfo?.mobileNumber} />
        <ReviewRow label="Contact Number" value={profileInfo?.contactNumber} />
        <ReviewRow label="Office Number" value={profileInfo?.officeNumber} />
        <ReviewRow label="Timezone" value={profileInfo?.timezone} />
      </Grid>

      <Grid
        size={12}
        sx={{
          border: "1px solid #E5E5E5",
          borderRadius: "8px",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            mb: 1,
            gap: 1,
            alignItems: { sm: "center" },
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Payment Settings</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={() => setStep(0)}
          >
            <EditIcon width="16px" active />
            <Typography
              sx={{ color: theme.palette.primary.main, fontSize: "14px" }}
            >
              Edit
            </Typography>
          </Box>
        </Box>
        <ReviewRow
          label="Payment Reminder"
          value={
            paymentReminderInfo
              ? paymentReminderInfo.enabled
                ? "Enabled"
                : "Disabled"
              : undefined
          }
        />
        <ReviewRow
          label="Frequency (Days)"
          value={paymentReminderInfo?.frequencyDays}
        />
      </Grid>

      <Grid
        size={12}
        sx={{
          border: "1px solid #E5E5E5",
          borderRadius: "8px",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Wash Types ({enabledWashTypes.length} enabled)
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={() => setStep(1)}
          >
            <EditIcon width="16px" active />
            <Typography
              sx={{ color: theme.palette.primary.main, fontSize: "14px" }}
            >
              Edit
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {enabledWashTypes.map((wt) => (
            <Box
              key={wt.code}
              sx={{
                backgroundColor: "#EAF3FF",
                color: theme.palette.primary.main,
                fontSize: "12px",
                borderRadius: "4px",
                px: 1,
                py: 0.5,
              }}
            >
              {wt.code} · {wt.name}
            </Box>
          ))}
        </Box>
      </Grid>

      <Grid
        size={12}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Button
          label="Back"
          variantType="secondary"
          sx={{
            padding: "0.5rem",
            px: "1.5rem",
            width: { xs: "100%", sm: "auto" },
          }}
          onClick={() => setStep(1)}
          disabled={isCreating}
        />
        <Button
          label={isCreating ? "Creating..." : "Create Organization"}
          variantType="primary"
          sx={{
            padding: "0.5rem",
            px: "1.5rem",
            width: { xs: "100%", sm: "auto" },
          }}
          onClick={handleCreate}
          disabled={isCreating}
        />
      </Grid>
    </Grid>
  );
};
